import React, { useEffect, useMemo, useState } from 'react';
import { ConfigProvider } from 'tea-component';
import { useIntl } from 'umi';
import moment, { Moment } from 'moment';
import RangePicker from '@home/components/RangePicker';
import { shortcutOption } from '@home/components/RangePicker/_config';
import { LANGUAGE_EN } from '@home/_config/config';
import {
  formatPrDayRangeForApi,
  formatPrMonthRangeForApi,
  getDefaultPrCurrentMonthRange,
  getDefaultPrDayT0Range,
} from '@home/pages/studio/pr/prRangeTimeFormat';
import { getPrGamesWithChannels } from '@home/services/studio';
import Loading from '@/pages/dashboard/components/Loadings';
import type { IPrGameChannelItem } from '@home/pages/studio/pr/_utils';
import { PR_HUB_FALLBACK_UNIFIED_EDITION_IDS } from '@home/pages/studio/pr/prHubFallbackGames';
import './index.scss';

/**
 * 筛选状态仅放在组件内：`CommonPageRender` 在切换左侧游戏时已 `key`（含 `prHubGameCode`）重挂整页，
 * 不会再使用模块级缓存，否则会跨游戏套用上一款的粒度/区间/isDateChanged。
 */

export interface IPrStudioMonthFilterProps {
  children?: React.ReactNode;
  /** 与 Home Filter 一致，用于 RangePicker 与默认区间 */
  timeZone?: number;
  /** 由 Render 传入的 data，包含 frameCode / studioData 等 */
  data?: Record<string, unknown>;
  /** 兜底 unified_edition_id 列表，仅在 studioData.game_list 为空时使用 */
  defaultUnifiedEditionIds?: string[];
}

type TPrGranularity = 'date' | 'month';

/** filterVal：与 API 对齐，输出 start_date / end_date (YYYY-MM-DD) / time_granularity */
export type TPrFilterVal = {
  start_date: string;
  end_date: string;
  time_granularity: 'daily' | 'monthly';
};

function isValidRangeValue(value: unknown): value is [Moment, Moment] {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    moment.isMoment(value[0]) &&
    value[0].isValid() &&
    moment.isMoment(value[1]) &&
    value[1].isValid()
  );
}

/** 将 API 返回的 datetime 格式（YYYY-MM-DD HH:mm:ss）截取为 YYYY-MM-DD */
function toDateString(datetime: string): string {
  return (datetime || '').slice(0, 10);
}

type TPrStudioGameRow = {
  unified_edition_id?: string;
};

type TPrStudioDataWithGameList = {
  game_list?: TPrStudioGameRow[];
};

export default function PrStudioMonthFilter(props: IPrStudioMonthFilterProps) {
  const {
    children,
    timeZone = 0,
    data,
    defaultUnifiedEditionIds = PR_HUB_FALLBACK_UNIFIED_EDITION_IDS,
  } = props;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;

  const [granularity, setGranularity] = useState<TPrGranularity>('month');
  const [timeValue, setTimeValue] = useState<[Moment, Moment]>(() =>
    getDefaultPrCurrentMonthRange(timeZone),
  );

  /** 游戏 + 渠道数据（由筛选器统一请求） */
  const [prGamesChannels, setPrGamesChannels] = useState<IPrGameChannelItem[]>([]);
  const [filterLoading, setFilterLoading] = useState(true);

  /** 同粒度下用户改过区间则为 true（trend 按筛选区间请求/展示）；月↔日切换时置 false，走默认 15 天/7 个月（buildPrTrendPayload） */
  const [isDateChanged, setIsDateChanged] = useState(false);

  const defaultValue = useMemo(
    () => ({
      month: getDefaultPrCurrentMonthRange(timeZone),
      date: getDefaultPrDayT0Range(timeZone),
    }),
    [timeZone],
  );

  /** filterVal：输出 API 所需的 start_date / end_date / time_granularity */
  const filterVal: TPrFilterVal = useMemo(() => {
    if (granularity === 'month') {
      const [start, end] = formatPrMonthRangeForApi(timeValue);
      return {
        start_date: toDateString(start),
        end_date: toDateString(end),
        time_granularity: 'monthly' as const,
      };
    }
    const [start, end] = formatPrDayRangeForApi(timeValue);
    return {
      start_date: toDateString(start),
      end_date: toDateString(end),
      time_granularity: 'daily' as const,
    };
  }, [granularity, timeValue]);

  const latestDate = useMemo(
    () => ({
      month: moment().utcOffset(timeZone),
      date: moment().utcOffset(timeZone).subtract(1, 'day'),
      week: moment().utcOffset(timeZone),
    }),
    [timeZone],
  );

  /** 请求游戏列表 + 渠道枚举 */
  useEffect(() => {
    const studioCode = data?.frameCode as string | undefined;
    const studioDataRaw = data?.studioData as TPrStudioDataWithGameList | undefined;
    const unifiedEditionIds = studioDataRaw?.game_list
      ?.map((g: TPrStudioGameRow) => g?.unified_edition_id)
      ?.filter(Boolean) as string[] | undefined;
    let unifiedEditionIdsList: string[] = unifiedEditionIds || [];
    if(!unifiedEditionIds?.length) {
      unifiedEditionIdsList = defaultUnifiedEditionIds;
    }
    if (!studioCode || !unifiedEditionIdsList?.length) {
      setFilterLoading(false);
      return;
    }
    setFilterLoading(true);
    getPrGamesWithChannels({
      studio_code: studioCode,
      unified_edition_ids: unifiedEditionIdsList,
    })
      .then((res: any) => {
        const games = res?.data?.games ?? res?.games ?? [];
        setPrGamesChannels(Array.isArray(games) ? games : []);
      })
      .catch(() => {
        setPrGamesChannels([]);
      })
      .finally(() => {
        setFilterLoading(false);
      });
  }, [data?.frameCode, data?.studioData]);

  const handleRangeChange = (value: unknown, typeArg?: string) => {
    if (
      (typeArg === 'month' || typeArg === 'date') &&
      typeArg !== granularity
    ) {
      const nextGranularity = typeArg;
      /** 月↔日：趋势固定走默认策略——日粒度最近 15 天、月粒度最近 7 个月（见 buildPrTrendPayload / trimPrTrendDefaultWindow） */
      setIsDateChanged(false);
      setGranularity(nextGranularity);
      if (nextGranularity === 'month') {
        setTimeValue(getDefaultPrCurrentMonthRange(timeZone));
      } else {
        setTimeValue(getDefaultPrDayT0Range(timeZone));
      }
      return;
    }

    setIsDateChanged(true);
    if (isValidRangeValue(value)) {
      setTimeValue(value);
    }
  };

  useEffect(() => {
    if (granularity === 'month') {
      setTimeValue(getDefaultPrCurrentMonthRange(timeZone));
    } else {
      setTimeValue(getDefaultPrDayT0Range(timeZone));
    }
  }, [timeZone]);

  return (
    <ConfigProvider locale={isEn ? 'zh' : 'en'}>
      <div className="dbh-studio-pr-month-filter dbo-ingame-analytics">
        <div className="dbh-studio-operate dbh-studio-home-filter">
          <div className="search-item search-item-date">
            <RangePicker
              type={granularity}
              value={timeValue}
              defaultValue={defaultValue}
              className="search-border"
              typeList={['month', 'date']}
              language={isEn ? 'en' : 'cn'}
              showSelectType
              startOfWeek={0}
              onChange={handleRangeChange}
              showShortcutTypeTitle
              overlayClassName="dbh-studio-home-range-picker-overlay"
              setShortcutOption={() => shortcutOption('', latestDate)}
            />
          </div>
        </div>
        {filterLoading
          ? <Loading className="dbh-studio-pr-filter-loading" />
          : children && React.Children.map(children, (child) => {
            if (!React.isValidElement(child)) {
              return child;
            }
            const inject: Partial<Record<string, unknown>> = {
              filterVal,
              prGamesChannels,
              filterLoading,
              isDateChanged,
              /** prGamesChannels / isDateChanged 注入 data，供下层 Service trackData → beforeRequest 使用 */
              data: {
                ...(data as Record<string, unknown>),
                prGamesChannels,
                isDateChanged,
              },
            };
            return React.cloneElement(child, inject);
          })}
      </div>
    </ConfigProvider>
  );
}
